// mds-dojo — dojo/_base/fx — FAITHFUL port of references/dojo/_base/fx.js.
// The dojo Animation engine: _Line, Animation (setInterval-driven timer via
// aspect.after on a shared runner), animateProperty, _fade, fadeIn, fadeOut, anim,
// _defaultEasing. AMD → ESM.
//
// Faithful adaptations (flagged):
//  1. dojo/sniff (has) import DROPPED. The only has() reads were has("ie")
//     (IE fade "tickle" in _makeFadeable — dropped, evergreen no-op like dojo's
//     non-IE branch) and has("extend-dojo") (guarded the `_mixin(dojo, basefx)`
//     that publishes dojo._defaultEasing / dojo._Animation). The guard is DROPPED
//     so the mixin always runs — animateProperty() reads `dojo._defaultEasing`, so
//     it MUST be present (this matches dojo's standard extend-dojo=true build).
//  2. Color.blendColors is NOT exposed by mds's dojo/_base/Color, so a faithful
//     local blendColors() (line-for-line from dojo/_base/Color.js) is used inside
//     PropLine.getValue for color-property tweens. Behaviour identical; contained
//     to this module (Color.ts left untouched).
import dojo from './kernel';
import config from './config';
import lang from './lang';
import Evented from '../Evented';
import Color from './Color';
import aspect from '../aspect';
import dom from '../dom';
import style from '../dom-style';

var _mixin = lang.mixin;

// Faithful local port of dojo/_base/Color.js Color.blendColors (mds Color omits it).
function blendColors(start: any, end: any, weight: any, obj?: any): any {
  var t = obj || new (Color as any)();
  lang.mixin(t, { r: 0, g: 0, b: 0, a: 1 });
  ['r', 'g', 'b', 'a'].forEach(function (x: string) {
    (t as any)[x] = (start as any)[x] + ((end as any)[x] - (start as any)[x]) * weight;
    if (x != 'a') { (t as any)[x] = Math.round((t as any)[x]); }
  });
  return t.sanitize();
}

// Module export
var basefx: any = {
  // summary:
  //		This module defines the base dojo/_base/fx implementation.
};

var _Line: any = basefx._Line = function (this: any, start: any, end: any) {
  // summary:
  //		Object used to generate values from a start value to an end value
  this.start = start;
  this.end = end;
};

_Line.prototype.getValue = function (n: any) {
  // summary:
  //		Returns the point on the line
  return ((this.end - this.start) * n) + this.start; // Decimal
};

var Animation: any = basefx.Animation = function (this: any, args: any) {
  // summary:
  //		A generic animation class that fires callbacks into its handlers
  //		object at various states.
  _mixin(this, args);
  if (lang.isArray(this.curve)) {
    this.curve = new _Line(this.curve[0], this.curve[1]);
  }

};
Animation.prototype = new (Evented as any)();

lang.extend(Animation, {
  // duration: Integer
  //		The time in milliseconds the animation will take to run
  duration: 350,

  // repeat: Integer?
  //		The number of times to loop the animation
  repeat: 0,

  // rate: Integer?
  //		the time in milliseconds to wait before advancing to next frame
  //		(used as a fps timer: 1000/rate = fps)
  rate: 20 /* 50 fps */,

  _percent: 0,
  _startRepeatCount: 0,

  _getStep: function (this: any) {
    var _p = this._percent,
      _e = this.easing
      ;
    return _e ? _e(_p) : _p;
  },
  _fire: function (this: any, evt: any, args: any) {
    // summary:
    //		Convenience function.  Fire event "evt" and pass it the
    //		arguments specified in "args".
    var a = args || [];
    if (this[evt]) {
      if (config.debugAtAllCosts) {
        this[evt].apply(this, a);
      } else {
        try {
          this[evt].apply(this, a);
        } catch (e) {
          // squelch and log because we shouldn't allow exceptions in
          // synthetic event handlers to cause the internal timer to run
          // amuck, potentially pegging the CPU.
          console.error('exception in animation handler for:', evt);
          console.error(e);
        }
      }
    }
    return this; // Animation
  },

  play: function (this: any, delay: any, gotoStart: any) {
    // summary:
    //		Start the animation.
    var _t = this;
    if (_t._delayTimer) { _t._clearTimer(); }
    if (gotoStart) {
      _t._stopTimer();
      _t._active = _t._paused = false;
      _t._percent = 0;
    } else if (_t._active && !_t._paused) {
      return _t;
    }

    _t._fire('beforeBegin', [_t.node]);

    var de = delay || _t.delay,
      _p = lang.hitch(_t, '_play', gotoStart);

    if (de > 0) {
      _t._delayTimer = setTimeout(_p, de);
      return _t;
    }
    _p();
    return _t;	// Animation
  },

  _play: function (this: any, gotoStart: any) {
    var _t = this;
    if (_t._delayTimer) { _t._clearTimer(); }
    _t._startTime = new Date().valueOf();
    if (_t._paused) {
      _t._startTime -= _t.duration * _t._percent;
    }

    _t._active = true;
    _t._paused = false;
    var value = _t.curve.getValue(_t._getStep());
    if (!_t._percent) {
      if (!_t._startRepeatCount) {
        _t._startRepeatCount = _t.repeat;
      }
      _t._fire('onBegin', [value]);
    }

    _t._fire('onPlay', [value]);

    _t._cycle();
    return _t; // Animation
  },

  pause: function (this: any) {
    // summary:
    //		Pauses a running animation.
    var _t = this;
    if (_t._delayTimer) { _t._clearTimer(); }
    _t._stopTimer();
    if (!_t._active) { return _t; /*Animation*/ }
    _t._paused = true;
    _t._fire('onPause', [_t.curve.getValue(_t._getStep())]);
    return _t; // Animation
  },

  gotoPercent: function (this: any, percent: any, andPlay: any) {
    // summary:
    //		Sets the progress of the animation.
    var _t = this;
    _t._stopTimer();
    _t._active = _t._paused = true;
    _t._percent = percent;
    if (andPlay) { _t.play(); }
    return _t; // Animation
  },

  stop: function (this: any, gotoEnd: any) {
    // summary:
    //		Stops a running animation.
    var _t = this;
    if (_t._delayTimer) { _t._clearTimer(); }
    if (!_t._timer) { return _t; /* Animation */ }
    _t._stopTimer();
    if (gotoEnd) {
      _t._percent = 1;
    }
    _t._fire('onStop', [_t.curve.getValue(_t._getStep())]);
    _t._active = _t._paused = false;
    return _t; // Animation
  },

  destroy: function (this: any) {
    // summary:
    //		cleanup the animation
    this.stop();
  },

  status: function (this: any) {
    // summary:
    //		Returns a string token representation of the status of
    //		the animation, one of: "paused", "playing", "stopped"
    if (this._active) {
      return this._paused ? 'paused' : 'playing'; // String
    }
    return 'stopped'; // String
  },

  _cycle: function (this: any) {
    var _t = this;
    if (_t._active) {
      var curr = new Date().valueOf();
      // Allow durations of 0 (instant) by setting step to 1 - see #13798
      var step = _t.duration === 0 ? 1 : (curr - _t._startTime) / (_t.duration);

      if (step >= 1) {
        step = 1;
      }
      _t._percent = step;

      // Perform easing
      if (_t.easing) {
        step = _t.easing(step);
      }

      _t._fire('onAnimate', [_t.curve.getValue(step)]);

      if (_t._percent < 1) {
        _t._startTimer();
      } else {
        _t._active = false;

        if (_t.repeat > 0) {
          _t.repeat--;
          _t.play(null, true);
        } else if (_t.repeat == -1) {
          _t.play(null, true);
        } else {
          if (_t._startRepeatCount) {
            _t.repeat = _t._startRepeatCount;
            _t._startRepeatCount = 0;
          }
        }
        _t._percent = 0;
        _t._fire('onEnd', [_t.node]);
        !_t.repeat && _t._stopTimer();
      }
    }
    return _t; // Animation
  },

  _clearTimer: function (this: any) {
    // summary:
    //		Clear the play delay timer
    clearTimeout(this._delayTimer);
    delete this._delayTimer;
  }

});

// the local timer, stubbed into all Animation instances
var ctr = 0,
  timer: any = null,
  runner: any = {
    run: function () { /* aspect.after target */ }
  };

lang.extend(Animation, {

  _startTimer: function (this: any) {
    if (!this._timer) {
      this._timer = aspect.after(runner, 'run', lang.hitch(this, '_cycle'), true);
      ctr++;
    }
    if (!timer) {
      timer = setInterval(lang.hitch(runner, 'run'), this.rate);
    }
  },

  _stopTimer: function (this: any) {
    if (this._timer) {
      this._timer.remove();
      this._timer = null;
      ctr--;
    }
    if (ctr <= 0) {
      clearInterval(timer);
      timer = null;
      ctr = 0;
    }
  }

});

// has("ie") IE fade "tickle" dropped (evergreen); dojo's non-IE branch is a no-op.
var _makeFadeable = function () { /* no-op (non-IE) */ };

basefx._fade = function (args: any) {
  // summary:
  //		Returns an animation that will fade the node defined by
  //		args.node from the start to end values passed (args.start
  //		args.end) (end is mandatory, start is optional)

  args.node = dom.byId(args.node);
  var fArgs = _mixin({ properties: {} }, args),
    props: any = (fArgs.properties.opacity = {});

  props.start = !('start' in fArgs) ?
    function () {
      return +style.get(fArgs.node, 'opacity') || 0;
    } : fArgs.start;
  props.end = fArgs.end;

  var anim = basefx.animateProperty(fArgs);
  aspect.after(anim, 'beforeBegin', lang.partial(_makeFadeable, fArgs.node), true);

  return anim; // Animation
};

basefx.fadeIn = function (args: any) {
  // summary:
  //		Returns an animation that will fade node defined in 'args' from
  //		its current opacity to fully opaque.
  return basefx._fade(_mixin({ end: 1 }, args)); // Animation
};

basefx.fadeOut = function (args: any) {
  // summary:
  //		Returns an animation that will fade node defined in 'args'
  //		from its current opacity to fully transparent.
  return basefx._fade(_mixin({ end: 0 }, args)); // Animation
};

basefx._defaultEasing = function (n: any) {
  // summary:
  //		The default easing function for Animation(s)
  return 0.5 + ((Math.sin((n + 1.5) * Math.PI)) / 2);	// Decimal
};

var PropLine: any = function (this: any, properties: any) {
  // PropLine is an internal class which is used to model the values of
  // an a group of CSS properties across an animation lifecycle.
  this._properties = properties;
  for (var p in properties) {
    var prop = properties[p];
    if (prop.start instanceof Color) {
      // create a reusable temp color object to keep intermediate results
      prop.tempColor = new (Color as any)();
    }
  }
};

PropLine.prototype.getValue = function (r: any) {
  var ret: any = {};
  for (var p in this._properties) {
    var prop = this._properties[p],
      start = prop.start;
    if (start instanceof Color) {
      ret[p] = blendColors(start, prop.end, r, prop.tempColor).toCss();
    } else if (!lang.isArray(start)) {
      ret[p] = ((prop.end - start) * r) + start + (p != 'opacity' ? prop.units || 'px' : 0);
    }
  }
  return ret;
};

basefx.animateProperty = function (args: any) {
  // summary:
  //		Returns an animation that will transition the properties of
  //		node defined in `args` depending how they are defined in
  //		`args.properties`

  var n = args.node = dom.byId(args.node);
  if (!args.easing) { args.easing = (dojo as any)._defaultEasing; }

  var anim = new Animation(args);
  aspect.after(anim, 'beforeBegin', lang.hitch(anim, function (this: any) {
    var pm: any = {};
    for (var p in this.properties) {
      // Make shallow copy of properties into pm because we overwrite
      // some values below. In particular if start/end are functions
      // we don't want to overwrite them or the functions won't be
      // called if the animation is reused.
      if (p == 'width' || p == 'height') {
        this.node.display = 'block';
      }
      var prop = this.properties[p];
      if (lang.isFunction(prop)) {
        prop = prop(n);
      }
      prop = pm[p] = _mixin({}, (lang.isObject(prop) ? prop : { end: prop }));

      if (lang.isFunction(prop.start)) {
        prop.start = prop.start(n);
      }
      if (lang.isFunction(prop.end)) {
        prop.end = prop.end(n);
      }
      var isColor = (p.toLowerCase().indexOf('color') >= 0);
      var getStyle = function getStyle(node: any, p: any) {
        // domStyle.get(node, "height") can return "auto" or "" on IE; this is more reliable:
        var v = ({ height: node.offsetHeight, width: node.offsetWidth } as any)[p];
        if (v !== undefined) { return v; }
        v = style.get(node, p);
        return (p == 'opacity') ? +v : (isColor ? v : parseFloat(v));
      };
      if (!('end' in prop)) {
        prop.end = getStyle(n, p);
      } else if (!('start' in prop)) {
        prop.start = getStyle(n, p);
      }

      if (isColor) {
        prop.start = new (Color as any)(prop.start);
        prop.end = new (Color as any)(prop.end);
      } else {
        prop.start = (p == 'opacity') ? +prop.start : parseFloat(prop.start);
      }
    }
    this.curve = new PropLine(pm);
  }), true);
  aspect.after(anim, 'onAnimate', lang.hitch(style, 'set', anim.node), true);
  return anim; // Animation
};

basefx.anim = function (node: any, properties: any, duration: any, easing: any, onEnd: any, delay: any) {
  // summary:
  //		A simpler interface to `animateProperty()`, also returns
  //		an instance of `Animation` but begins the animation
  //		immediately, unlike nearly every other Dojo animation API.
  return basefx.animateProperty({ // Animation
    node: node,
    duration: duration || Animation.prototype.duration,
    properties: properties,
    easing: easing,
    onEnd: onEnd
  }).play(delay || 0);
};


// has("extend-dojo") guard dropped: publish onto the dojo kernel unconditionally so
// dojo._defaultEasing (read by animateProperty) and dojo._Animation exist, matching
// the standard extend-dojo=true dojo build.
_mixin(dojo, basefx);
// Alias to drop come 2.0:
(dojo as any)._Animation = Animation;

export default basefx;
